import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, Plus, X } from 'lucide-react';
import { apiClient } from '../../api';
import { useChat } from '../../contexts/ChatContext';

export const COMMANDS = [
  ['/correlate', 'Find related events across hosts and time'],
  ['/timeline', 'Build a timeline for a host, finding or case'],
  ['/summarise', 'Summarise a case, a run or the last 24 hours'],
  ['/report', 'Draft an incident report you can save to a case'],
  ['/playbook', 'Suggest a playbook and fill in its target'],
];

const KINDS = [['finding', 'Finding'], ['ioc', 'IoC alert'], ['cve', 'CVE alert'], ['case', 'Case'], ['asset', 'Asset'], ['run', 'Playbook run']];
const CHIP_KIND = { page: 'PAGE', finding: 'FINDING', ioc: 'IOC', cve: 'CVE', case: 'CASE', asset: 'ASSET', run: 'RUN' };

async function loadOptions(kind) {
  const hostMap = async () => {
    const r = await apiClient.get('/api/servers?state=REGISTERED').catch(() => null);
    return Object.fromEntries((r?.data?.servers || []).map(s => [s.id, s.hostname]));
  };
  if (kind === 'finding') {
    const r = await apiClient.get('/api/findings?status=new,triaged,investigating,contained&limit=100');
    return (r.data?.findings || []).map(f => ({ id: f.id, label: f.id, sub: `${f.title} · ${f.server_hostname || ''}` }));
  }
  if (kind === 'ioc' || kind === 'cve') {
    const [r, hosts] = await Promise.all([apiClient.get(`/api/alerts/${kind}?resolved=0`), hostMap()]);
    const list = kind === 'ioc' ? r.data?.ioc_alerts || [] : r.data?.cve_alerts || [];
    return list.slice(0, 100).map(a => ({ id: String(a.id), label: `#${a.id}`, sub: `${kind === 'ioc' ? `${a.ioc_type} ${a.ioc_value}` : `${a.cve_id} · ${a.package_name}`} · ${hosts[a.server_id] || ''}` }));
  }
  if (kind === 'case') {
    const r = await apiClient.get('/api/cases?limit=100');
    return (r.data?.cases || []).map(c => ({ id: c.id, label: c.title.length > 28 ? `${c.title.slice(0, 28)}…` : c.title, sub: `${c.status} · ${c.priority}` }));
  }
  if (kind === 'asset') {
    const r = await apiClient.get('/api/servers?state=REGISTERED');
    return (r.data?.servers || []).map(s => ({ id: s.id, label: s.hostname, sub: [s.ip_address, s.os_info].filter(Boolean).join(' · ') }));
  }
  const r = await apiClient.get('/api/workflows/runs');
  return (r.data?.runs || []).map(x => ({ id: x.id, label: `RUN-${x.id.slice(0, 8).toUpperCase()}`, sub: `${x.workflow_name || 'Playbook'} · ${x.status}` }));
}

export const ContextPicker = ({ onClose, anchor = 'up' }) => {
  const { addChip } = useChat();
  const [kind, setKind] = useState('finding');
  const [opts, setOpts] = useState(null);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    setOpts(null);
    loadOptions(kind).then(setOpts).catch(() => setOpts([]));
  }, [kind]);
  useEffect(() => {
    const close = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [onClose]);
  const needle = q.trim().toLowerCase();
  const shown = (opts || []).filter(o => !needle || `${o.id} ${o.label} ${o.sub}`.toLowerCase().includes(needle)).slice(0, 40);
  return (
    <div ref={ref} role="dialog" aria-label="Add context" className={`absolute left-0 right-0 ${anchor === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'} z-30 flex flex-col gap-2 p-2.5 rounded-xl border`} style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}>
      <div className="flex gap-2">
        <label htmlFor="ctx-kind" className="sr-only">Kind</label>
        <select id="ctx-kind" value={kind} onChange={e => setKind(e.target.value)} className="h-9 px-2 rounded-lg border text-[13px]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
          {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label htmlFor="ctx-q" className="sr-only">Search</label>
        <input id="ctx-q" autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search" className="flex-grow min-w-0 h-9 px-2.5 rounded-lg border text-[13px] outline-none focus:border-[color:var(--accent)]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }} />
      </div>
      <div className="max-h-[240px] overflow-y-auto flex flex-col">
        {opts === null && <span className="px-2 py-2 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Loading…</span>}
        {opts && !shown.length && <span className="px-2 py-2 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Nothing found.</span>}
        {shown.map(o => (
          <button key={o.id} type="button" onClick={() => { addChip({ kind, id: o.id, label: o.label }); onClose(); }} className="flex flex-col gap-0.5 px-2 py-1.5 rounded-md text-left hover:bg-[color:var(--surf-raised)]">
            <span className="font-mono text-[12px]">{o.label}</span>
            <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{o.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ContextChips = ({ compact = false }) => {
  const { attached, removeChip } = useChat();
  const [picking, setPicking] = useState(false);
  return (
    <div className="relative flex items-center gap-1.5 flex-wrap">
      {!compact && <span className="font-mono text-[10px] tracking-[0.12em] mr-0.5" style={{ color: 'var(--ink-faint)' }}>USING</span>}
      {attached.map(c => (
        <span key={`${c.kind}:${c.id}`} className="h-7 box-border inline-flex items-center gap-1.5 pl-[9px] pr-0.5 rounded-[7px] border text-xs max-w-full" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
          <span className="font-mono text-[9.5px] tracking-[0.08em]" style={{ color: 'var(--ink-faint)' }}>{CHIP_KIND[c.kind] || c.kind.toUpperCase()}</span>
          <span className="font-mono text-[11.5px] truncate max-w-[180px]">{c.label || c.id}</span>
          <button type="button" onClick={() => removeChip(c)} aria-label={`Remove ${c.label || c.id} from context`} className="w-[22px] h-[22px] flex items-center justify-center" style={{ color: 'var(--ink-faint)' }}><X className="w-2.5 h-2.5" strokeWidth={2.4} /></button>
        </span>
      ))}
      {!attached.length && <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Nothing attached · answers use the whole organisation</span>}
      <button type="button" onClick={() => setPicking(v => !v)} aria-expanded={picking} className="h-7 inline-flex items-center gap-1 px-2 rounded-[7px] border border-dashed text-xs" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-muted)' }}>
        <Plus className="w-[11px] h-[11px]" strokeWidth={2.2} />Context
      </button>
      {picking && <ContextPicker anchor="down" onClose={() => setPicking(false)} />}
    </div>
  );
};

export const ModelFooter = () => {
  const { config } = useChat();
  if (!config) return null;
  if (!config.configured) return <div className="text-[11.5px]" style={{ color: 'var(--sev-crit-fg)' }}>No AI model is configured. An admin can connect one in Settings › Integrations.</div>;
  return (
    <div className="flex items-center gap-2 text-[11.5px] min-w-0" style={{ color: 'var(--ink-faint)' }}>
      <span className="h-[26px] inline-flex items-center gap-1.5 px-2 rounded-md border font-mono text-[11px] shrink-0 max-w-[60%]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }} title="Set by an admin in Settings › Integrations">
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: config.local ? 'var(--sev-good-fg)' : 'var(--sev-high-fg)' }} />
        <span className="truncate">{config.provider === 'openrouter' ? 'OpenRouter' : config.provider === 'claude' ? 'Claude' : 'Ollama'} · {config.model}</span>
      </span>
      <span className="truncate">{config.local ? 'Stays on your network' : 'Sends context outside your network'}</span>
      <span className="ml-auto shrink-0 hidden sm:inline">Changes ask first</span>
    </div>
  );
};

export default function Composer({ placeholder, wide = false }) {
  const { send, live, config } = useChat();
  const [text, setText] = useState('');
  const [picking, setPicking] = useState(false);
  const [cmds, setCmds] = useState(false);
  const taRef = useRef(null);
  const slash = text.startsWith('/') && !text.includes(' ');
  const matches = COMMANDS.filter(([k]) => k.startsWith(text.trim() || '/'));
  const showCmds = cmds || (slash && matches.length > 0);
  const submit = () => {
    if (!text.trim() || live) return;
    send(text);
    setText('');
    setCmds(false);
  };
  const pick = (k) => { setText(`${k} `); setCmds(false); taRef.current?.focus(); };
  return (
    <div className="relative flex flex-col gap-1.5 pt-2.5 pb-2 pl-3 pr-2.5 rounded-xl border" style={{ background: wide ? 'var(--surf-surface)' : 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)' }}>
      {picking && <ContextPicker onClose={() => setPicking(false)} />}
      {showCmds && (
        <div role="listbox" aria-label="Commands" className="absolute left-0 right-0 bottom-full mb-2 z-30 p-1 rounded-xl border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}>
          {(slash ? matches : COMMANDS).map(([k, d]) => (
            <button key={k} type="button" role="option" aria-selected="false" onMouseDown={e => { e.preventDefault(); pick(k); }} className="w-full grid grid-cols-[96px_minmax(0,1fr)] gap-2.5 px-2.5 py-2 rounded-md text-left text-[12.5px] hover:bg-[color:var(--surf-raised)]">
              <span className="font-mono text-xs" style={{ color: 'var(--ink-link)' }}>{k}</span><span style={{ color: 'var(--ink-muted)' }}>{d}</span>
            </button>
          ))}
        </div>
      )}
      <label htmlFor={wide ? 'ask-full' : 'ask'} className="sr-only">Message the assistant</label>
      <textarea
        id={wide ? 'ask-full' : 'ask'}
        ref={taRef}
        rows={2}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (slash && matches.length === 1 && text.trim() !== matches[0][0]) pick(matches[0][0]); else submit(); }
          if (e.key === 'Escape') setCmds(false);
        }}
        placeholder={placeholder}
        disabled={config && !config.configured}
        className="box-border w-full p-0 bg-transparent border-0 outline-none text-[13.5px] leading-normal resize-none disabled:opacity-50"
        style={{ color: 'var(--ink-text)' }}
      />
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => setPicking(v => !v)} aria-expanded={picking} className="h-[30px] flex items-center gap-[5px] px-2 rounded-[7px] border text-xs" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}><Plus className="w-[11px] h-[11px]" strokeWidth={2.2} />Context</button>
        <button type="button" onClick={() => setCmds(v => !v)} aria-expanded={showCmds} className="h-[30px] px-2 rounded-[7px] border font-mono text-[11.5px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>/ Commands</button>
        <button type="button" onClick={submit} disabled={!text.trim() || !!live} aria-label="Send" className="ml-auto w-9 h-9 flex items-center justify-center rounded-[9px] disabled:opacity-40" style={{ background: 'var(--accent)', color: '#0A0A0A' }}><ArrowUp className="w-[15px] h-[15px]" strokeWidth={2.2} /></button>
      </div>
    </div>
  );
}
